import { interval, Subject, Subscription, take, takeUntil } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { Application, ApplicationTypes } from "src/app/core/models/application";
import { ApplicationService } from "src/app/core/services/application.service";
import { NavigationService } from "src/app/core/services/navigation.service";
import { AuthenticationService } from "../../../../core/services/authentication.service";
import { MatDialog } from "@angular/material/dialog";
import { NgxSpinnerService } from "ngx-spinner";
import { AddApplicationComponent } from "../../modals/add-application/add-application.component";
import { DeleteApplicationComponent } from "../../modals/delete-application/delete-application.component";
import { UpdateApplicationComponent } from "../../modals/update-application/update-application.component";
import { UploadFileApplicationComponent } from "../../modals/upload-file-application/upload-file-application.component";
import { BuildFormJsonComponent } from "../../modals/build-form-json/build-form-json.component";
import { SyncConfigService } from "src/app/core/services/sync-config.service";
import { Entities, ErrorCodes } from "src/app/core/models/globals";
import { HeaderService } from "../../../../core/services/header.service";
import { UserRoleNames } from "../../../../core/models/users";
import { CsvExportService } from "src/app/core/services/csv-export.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SessionService } from "src/app/core/services/session.service";
import { ConfirmDialogComponent } from "src/app/modules/shared/components/confirm-dialog/confirm-dialog.component";

interface GroupedApplication {
  appName: string;
  versions: string[];
  selectedVersion: string;
  checkLicense: boolean;
  dateAdded: Date;
  applicationNodeGroups: string;
  applicationType: string;
  errorChips?: { value: string; tooltip: string }[];
}

@Component({
  selector: "app-application-summary",
  templateUrl: "./application-summary.component.html",
  styleUrls: ["./application-summary.component.scss"],
})
export class ApplicationSummaryComponent implements OnInit, OnDestroy {
  filterText: string = "";
  depName: string =
    this.route.snapshot.params["deploymentName"] ||
    this.route.parent.snapshot.params["deploymentName"];
  active = 1;
  available: boolean;
  p: number = 1; // count of pagination
  tempAppName: string;
  tempAppVer: string;
  currentEntity: string;
  detailsLoaded: boolean; // for checking if data is loaded
  applications: Application[];
  filteredApplications: GroupedApplication[];
  currentIndex: number = 0;
  errorMsg = "";
  // errorChips:  = [];
  timeSort: boolean = false; // filter
  activeSort: string = "none"; // for checking if any sort is active while, used when auto refreshing data
  selectedApplication: Application | null;
  sortName = 0;
  sortLicense = 0;
  selectedGroupedApplication: GroupedApplication | null;
  groupedData: { [appName: string]: any[] } = {};
  groupedDataArray: GroupedApplication[] | null = null;
  currentUser = this.authenticationService.currentUserValue;
  isMonitor: boolean =
    this.currentUser.role == UserRoleNames.Reader ? true : false;
  isGlobalAdmin: boolean = this.currentUser.role == UserRoleNames.GlobalAdmin;
  isReader: boolean = this.currentUser.role == UserRoleNames.Reader;
  destroy$ = new Subject<void>();
  detailsViewOpen: boolean = false;
  config = {
    itemsPerPage: 10,
    currentPage: 1,
  };

  constructor(
    private route: ActivatedRoute,
    private navService: NavigationService,
    private applicationService: ApplicationService,
    private router: Router,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private syncConfig: SyncConfigService,
    private headerService: HeaderService,
    private authenticationService: AuthenticationService,
    private csvExportService: CsvExportService,
    private snackBar: MatSnackBar,
    private _sessionService: SessionService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.spinner.show("applicationLoad");
    this.applicationService.tempDeployName = this.depName;
    this.navService.breadcrumbEntity.subscribe((x) => {
      this.currentEntity = x;
    });
    this.spinner.show();
    this.loadAppData();
    interval(this.syncConfig.config.applications.listReqInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        this.loadAppData();
        this.navService.sendLastUpdate(this.headerService.getUserNewDate());
      });
    this.navService.sendParentData(Entities.Applications);
    if (this.currentEntity != Entities.Applications) {
      this.navService.sendLeftPanelIndex(2);
    } else {
      this.navService.sendLeftPanelIndex(0);
    }
  }

  hideDetails() {
    this.detailsViewOpen = false;
  }

  handleAction(action: string) {
    switch (action) {
      case "Clone":
        this.addApplication(true);
        break;
      case "Build":
        this.buildForm();
        break;
      case "Update":
        this.updateApplication();
        break;
      case "Delete":
        this.deleteApplication();
        break;
      default:
        this.snackBar.open("Invalid action", "Ok", {
          duration: 3000,
        });
        break;
    }
  }

  loadAppData() {
    this.applicationService
      .getAllApplicationsForUser(this.depName)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.available = true;
          this.applications = <Application[]>JSON.parse(JSON.stringify(data));
          this.groupedData = {};

          // Group the data by appName
          for (const entry of this.applications) {
            const appName = entry.appName;
            if (!this.groupedData[appName]) {
              this.groupedData[appName] = [];
            }
            // check if entry is already present
            if (this.groupedData[appName].findIndex((x) => x == entry) == -1) {
              this.groupedData[appName].push(entry);
            }
          }

          let isGroupedDataArrayNull: boolean = this.groupedDataArray == null;

          this.groupedDataArray = Object.keys(this.groupedData).map(
            (appName) => ({
              appName,
              versions: this.groupedData[appName].map((x) => x.appVersionText),
              selectedVersion: isGroupedDataArrayNull
                ? this.groupedData[appName].map((x) => x.appVersionText)[0]
                : this.groupedDataArray.find((x) => x.appName == appName)
                    ?.selectedVersion ||
                  this.groupedData[appName].map((x) => x.appVersionText)[0],
              checkLicense: isGroupedDataArrayNull
                ? this.groupedData[appName][0].checkLicense
                : this.groupedDataArray.find((x) => x.appName == appName)
                    ?.checkLicense || this.groupedData[appName][0].checkLicense,
              dateAdded: isGroupedDataArrayNull
                ? this.groupedData[appName][0].dateAdded
                : this.groupedDataArray.find((x) => x.appName == appName)
                    ?.dateAdded || this.groupedData[appName][0].dateAdded,
              applicationNodeGroups: isGroupedDataArrayNull
                ? this.groupedData[appName][0].applicationNodeGroups
                : this.groupedDataArray.find((x) => x.appName == appName)
                    ?.applicationNodeGroups ||
                  this.groupedData[appName][0].applicationNodeGroups,
              applicationType: isGroupedDataArrayNull
                ? this.groupedData[appName][0].applicationType
                : this.groupedDataArray.find((x) => x.appName == appName)
                    ?.applicationType ||
                  this.groupedData[appName][0].applicationType,
              errorChips: isGroupedDataArrayNull
                ? this.getErrorChipsForApp(this.groupedData[appName][0])
                : this.groupedDataArray.find((x) => x.appName == appName)
                    ?.errorChips ||
                  this.getErrorChipsForApp(this.groupedData[appName][0]),
            })
          );

          if (!this.applications[this.currentIndex + (this.p * 10 - 10)]) {
            this.currentIndex--;
          }

          this.onSearch();
          let currIdx = this.currentIndex;
          if (this.p != 1) {
            if (
              !this.filteredApplications[this.currentIndex + (this.p * 10 - 10)]
            ) {
              this.currentIndex--;
            }
            currIdx = this.currentIndex + (this.p * 10 - 10);
          }
          this.selectedApplication =
            this.applications.find(
              (app) =>
                app.appName == this.filteredApplications[currIdx]?.appName &&
                app.appVersionText ==
                  this.filteredApplications[currIdx]?.selectedVersion
            ) || this.applications[0];

          this.applicationService.updateSelectedApplication(
            this.selectedApplication
          );

          this.router.navigate(
            [
              this.selectedApplication.appName,
              this.selectedApplication.appVersionText,
            ],
            { relativeTo: this.route }
          );

          this.selectedGroupedApplication =
            this.filteredApplications[currIdx] ||
            this.filteredApplications?.find(
              (fa) => fa.appName == this.selectedApplication?.appName
            );
          let updatedErrorChips = this.getErrorChipsForApp(
            this.selectedApplication
          );
          this.selectedGroupedApplication.errorChips = updatedErrorChips;
          this.selectedGroupedApplication.checkLicense = JSON.parse(
            this.selectedApplication.checkLicense.toLowerCase()
          );
          this.selectedGroupedApplication.applicationNodeGroups =
            this.selectedApplication.applicationNodeGroups;
          this.spinner.hide("applicationLoad");
          this.detailsLoaded = true;
        } else {
          this.available = false;
          this.applications = [];
          this.groupedDataArray = [];
          this.onSearch();
          this.errorMsg =
            "No Applications are available for selected deployment";
          this.spinner.hide("applicationLoad");
        }
      });
  }

  redirectTo(app: GroupedApplication, i) {
    let pageIdx =
      this.config.currentPage * this.config.itemsPerPage -
      this.config.itemsPerPage +
      i;
    this.selectedApplication = this.applications.find(
      (app) =>
        app.appName == this.filteredApplications[pageIdx].appName &&
        app.appVersionText == this.filteredApplications[pageIdx].selectedVersion
    );
    this.selectedGroupedApplication = app;
    this.currentIndex = pageIdx;
    this.applicationService.updateSelectedApplication(this.selectedApplication);
    this.router.navigate([app.appName, app.selectedVersion], {
      relativeTo: this.route,
    });
  }

  addApplication(autoFill: boolean = false) {
    const dialogRef = this.dialog.open(AddApplicationComponent, {
      width: "600px",
      autoFocus: false,
      disableClose: true,
      data: { application: autoFill ? this.selectedApplication : null, isClone: autoFill },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.spinner.show("applicationLoad");
        this.loadAppData();
      }
    });
  }

  deleteApplication() {
    const dialogRef = this.dialog.open(DeleteApplicationComponent, {
      disableClose: true,
      autoFocus: false,
      width: "400px",
      data: {
        title: "Delete Application",
        content: "Do you want to delete the Application ?",
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.spinner.show("applicationLoad");
        let delAppData = {
          appName: this.selectedApplication.appName,
          appVersion: this.selectedApplication.appVersionText,
        };
        this.applicationService
          .deleteApplication(delAppData, this.depName)
          .subscribe({
            next: (data) => {
              this.snackBar.open("Application deleted", "Ok", {
                duration: 3000,
              });
              this.refreshComponent();
              this.loadAppData();
            },
            error: (err) => {
              this.snackBar.open("Failed to delete application", "Ok", {
                duration: 3000,
              });
              this.spinner.hide("applicationLoad");
            },
          });
      }
    });
  }

  updateApplication() {
    const dialogRef = this.dialog.open(UpdateApplicationComponent, {
      width: "800px",
      autoFocus: false,
      disableClose: true,
      data: {
        application: this.selectedApplication,
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.spinner.show("applicationLoad");
        this.updateApplicationFiles(res?.updatedApp, res?.currApp);
        this.refreshComponent();
      }
    });
  }

  uploadFileApp() {
    const dialogRef = this.dialog.open(UploadFileApplicationComponent, {
      width: "450px",
      autoFocus: false,
      disableClose: true,
      data: {
        depName: this.depName,
        appName: this.selectedApplication.appName,
        appVer: this.selectedApplication.appVersionText,
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      this.refreshComponent();
    });
  }

  buildForm() {
    const dialogRef = this.dialog.open(BuildFormJsonComponent, {
      disableClose: true,
      autoFocus: false,
      width: "600px",
      height: "526px",
      data: {
        depName: this.depName,
        appName: this.selectedApplication.appName,
        appVer: this.selectedApplication.appVersionText,
      },
    });
  }

  sortLoadData() {
    if (this.timeSort) {
      this.filteredApplications.sort(
        (a, b) =>
          new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime()
      );
    }
  }

  sortByTime() {
    this.timeSort = !this.timeSort;
    this.activeSort = "time";
    if (this.timeSort) {
      this.sortLoadData();
    } else {
      this.filteredApplications.sort(
        (a, b) =>
          new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
      );
    }
  }

  sortByName() {
    if (this.sortName == 0) {
      this.filteredApplications.sort(
        (a, b) =>
          0 - (a.appName.toLowerCase() > b.appName.toLowerCase() ? 1 : -1)
      );
    }
    if (this.sortName == 1) {
      this.filteredApplications.sort(
        (a, b) =>
          0 - (b.appName.toLowerCase() > a.appName.toLowerCase() ? 1 : -1)
      );
    }
    this.sortName++;
    if (this.sortName == 2) {
      this.sortName = 0;
    }
  }

  sortByLicenseCheck() {
    if (this.sortLicense == 0) {
      this.filteredApplications.sort(
        (a, b) =>
          0 -
          (a.checkLicense.toString().toLowerCase() >
          b.checkLicense.toString().toLowerCase()
            ? 1
            : -1)
      );
    }
    if (this.sortLicense == 1) {
      this.filteredApplications.sort(
        (a, b) =>
          0 -
          (b.checkLicense.toString().toLowerCase() >
          a.checkLicense.toString().toLowerCase()
            ? 1
            : -1)
      );
    }
    this.sortLicense++;
    if (this.sortLicense == 2) {
      this.sortLicense = 0;
    }
  }

  refreshComponent() {
    let url = this.router.url;
    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      this.router.navigate([decodeURI(url)]);
    });
  }

  onSearch() {
    this.filteredApplications = [...this.groupedDataArray].filter((nd) =>
      nd.appName.toLowerCase().includes(this.filterText.toLowerCase())
    );
    this.p = 1;
  }

  downloadDataAsCsv() {
    const fileName = `applications_${this.depName}.csv`;
    const appData = [...this.applications];
    appData.forEach((app) => (app.errorChips = null));
    this.csvExportService.downloadDataAsCsv(appData, fileName);
  }

  getErrorChipsForApp(app: Application) {
    let chips = [];
    if (!app) return chips;
    if (!app.executeFile) {
      chips.push({
        value: "Execute",
        tooltip: `Execute file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    if (!app.cancelFile) {
      chips.push({
        value: "Cancel",
        tooltip: `Cancel file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    if (!app.resumeFile) {
      chips.push({
        value: "Resume",
        tooltip: `Resume file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    if (!app.suspendFile) {
      chips.push({
        value: "Suspend",
        tooltip: `Suspend file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    if (!app.licenseFile) {
      chips.push({
        value: "License",
        tooltip: `License file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    if (!app.graphFiles) {
      chips.push({
        value: "Graph",
        tooltip: `Graph file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    if (!app.customActionFile) {
      chips.push({
        value: "Custom",
        tooltip: `Custom Action file not found for ${app.appName}-${app.appVersionText}.`,
      });
    }
    return chips;
  }

  // Function to handle the change event
  onSelectionChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    let currApp = this.groupedData[
      this.filteredApplications[this.currentIndex].appName
    ].find((x) => x.appVersionText == target.value);

    // update groupedApplication parameters accordingly
    this.filteredApplications[this.currentIndex].selectedVersion = target.value;
    this.filteredApplications[this.currentIndex].checkLicense =
      currApp.checkLicense;
    this.filteredApplications[this.currentIndex].dateAdded = currApp.dateAdded;
    this.filteredApplications[this.currentIndex].applicationNodeGroups =
      currApp.applicationNodeGroups;
    this.filteredApplications[this.currentIndex].errorChips =
      this.getErrorChipsForApp(currApp);
    this.filteredApplications[this.currentIndex].applicationType =
      currApp.applicationType;
  }

  manageFiles(application: GroupedApplication) {
    this.dialog
      .open(UploadFileApplicationComponent, {
        width: "800px",
        autoFocus: false,
        disableClose: true,
        data: {
          application: this.applications.find(
            (app) =>
              app.appName == application.appName &&
              app.appVersionText == application.selectedVersion
          ),
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.spinner.show("applicationLoad");
          this.updateApplicationFiles(res?.updatedApp, res?.currApp);
        }
      });
  }

  accessApp(application: GroupedApplication) {
    this.dialog
      .open(ConfirmDialogComponent, {
        autoFocus: false,
        disableClose: false,
        width: "25%",
        data: {
          titleMessage: "Connect Application",
          bodyMessage: `Do you want to access application ${application.appName}-${application.selectedVersion} ?`,
          cancelText: "Cancel",
          submitText: "Connect",
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.spinner.show("applicationLoad");
          this._sessionService
            .requestSessionFromDeployment(
              this.depName,
              "",
              "",
              this.currentUser.username,
              ""
            )
            .pipe(take(1))
            .subscribe((data: any) => {
              if (data) {
                let response = JSON.parse(data["message"]);
                if (data.actionCode != ErrorCodes.Succeeded) {
                  this.snackBar.open(response.FileContent, "Ok", {
                    duration: 5000,
                  });
                  return;
                }

                if (response.VmPlatform.toLowerCase() == "windows") {
                  //this.download(response.VmName + ".rdp", response.FileContent);
                } else if (
                  response.VmPlatform.toLowerCase() == "linux" &&
                  response.UseVncFileOnConnect
                ) {
                  //this.download(response.VmName + ".vnc", response.FileContent);
                } else {
                  let url = response.FileContent;
                  window.open(url, "_blank", "noopener");
                }
                this.spinner.hide("applicationLoad");
              } else {
                this.snackBar.open("Something went wrong!", "Ok", {
                  duration: 5000,
                });
                this.spinner.hide("applicationLoad");
              }
            });
        }
      });
  }

  updateApplicationFiles(
    updatedApplication: Application,
    currApplication: Application
  ) {
    this.applicationService
      .updateApplication(currApplication.depName, updatedApplication)
      .subscribe({
        next: (data) => {
          this.snackBar.open("Application updated", "Ok", {
            duration: 3000,
          });
          if (
            updatedApplication.executeFile == "" ||
            updatedApplication.executeFile == null
          ) {
            setTimeout(() => {
              this.snackBar.open(
                "One Execute File is required to run the Job of Application",
                "Ok"
              );
            }, 3000);
          }
          this.loadAppData();
          this.refreshComponent();
        },
        error: (err) => {
          this.snackBar.open("Failed to update details", "Ok", {
            duration: 3000,
          });
          this.spinner.hide("applicationLoad");
        },
      });
  }

  getKeyByValue(value: number): string {
    return ApplicationTypes[value];
  }

  toggleView(application: Application) {
    if (
      this.selectedApplication.appName == application.appName &&
      this.detailsViewOpen
    ) {
      this.detailsViewOpen = false;
    } else {
      this.detailsViewOpen = true;
    }
  }
}
